/**
 * The door.
 *
 * ONE SCREEN, NOT TWO. Sign in and sign up are the same form with a different
 * verb: an email, a password, and a name that only appears when it is needed.
 * Two routes would mean the common mistake — arriving at the wrong one — costs
 * a page load and a re-typed password, and for a household of three people the
 * distinction is not one anybody holds in their head.
 *
 * WHAT THIS DOES NOT DO. There is no password-strength meter, no "confirm your
 * password" box and no captcha. The meter teaches people to satisfy a meter;
 * the confirm box is a workaround for not being able to see what you typed,
 * which the reveal button fixes properly; and the captcha is for a door
 * strangers can reach, which this one cannot yet.
 *
 * The Google button appears only when the server was given credentials for it
 * — asked of the server, because the browser has no way to know, and a button
 * that leads to a 500 is worse than no button.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { authApi, messageOf } from './api.js';

type Mode = 'in' | 'up';

interface Props {
  /** Whether the server has Google configured. */
  google: boolean;
  /** Re-read the session and enter the app. */
  onSignedIn: () => void;
}

export function SignIn({ google, onSignedIn }: Props) {
  const [mode, setMode] = useState<Mode>('in');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signingUp = mode === 'up';

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (signingUp) {
        await authApi.signUp({ name: name.trim(), email: email.trim(), password });
      } else {
        await authApi.signIn({ email: email.trim(), password });
      }
      onSignedIn();
    } catch (e: unknown) {
      setError(messageOf(e));
      setBusy(false);
    }
  }

  /** Switching modes keeps what was typed — it is the same form. */
  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
  }

  const ready =
    email.trim().length > 0 && password.length > 0 && (!signingUp || name.trim().length > 0);

  return (
    <main className="signin">
      <div className="signin-card">
        <h1>Excent</h1>
        <p className="hint">
          {signingUp
            ? 'Make an account for yourself. If this household has no owner yet, it becomes yours.'
            : 'Welcome back.'}
        </p>

        {google && (
          <>
            {/* A button, not a link, because the endpoint behind it is POST —
                it hands back a URL rather than redirecting, so there is nothing
                for an href to point at. */}
            <button
              type="button"
              className="signin-google"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setError(null);
                authApi
                  .startGoogle(window.location.origin)
                  .then(({ url }) => {
                    if (url) window.location.assign(url);
                    else {
                      setError('Google did not hand back a sign-in link.');
                      setBusy(false);
                    }
                  })
                  .catch((e: unknown) => {
                    setError(messageOf(e));
                    setBusy(false);
                  });
              }}
            >
              Continue with Google
            </button>
            <p className="signin-or" aria-hidden="true">
              <span>or</span>
            </p>
          </>
        )}

        <form onSubmit={(e) => void submit(e)}>
          {signingUp && (
            <label className="signin-field">
              <span className="field-label">Your name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Hasib"
                autoComplete="name"
                autoFocus
              />
            </label>
          )}

          <label className="signin-field">
            <span className="field-label">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus={!signingUp}
              required
            />
          </label>

          <label className="signin-field">
            <span className="field-label">Password</span>
            <span className="signin-password">
              <input
                type={reveal ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // The browser fills and saves the right one only if it is told
                // which this is. Getting it wrong is how a password manager
                // silently overwrites a good entry with a new-account one.
                autoComplete={signingUp ? 'new-password' : 'current-password'}
                minLength={signingUp ? 10 : undefined}
                required
              />
              {/* Reveal rather than a "confirm password" box: being able to
                  read what you typed solves the same problem and costs one
                  field instead of one more. */}
              <button
                type="button"
                className="chip chip-ghost"
                onClick={() => setReveal((r) => !r)}
                aria-pressed={reveal}
              >
                {reveal ? 'Hide' : 'Show'}
              </button>
            </span>
            {signingUp && <span className="hint">At least 10 characters.</span>}
          </label>

          {error && <p className="error">{error}</p>}

          <button type="submit" className="primary signin-submit" disabled={!ready || busy}>
            {busy ? 'One moment…' : signingUp ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="signin-switch">
          {signingUp ? (
            <>
              Already have an account?{' '}
              <button type="button" className="linkish" onClick={() => switchTo('in')}>
                Sign in
              </button>
            </>
          ) : (
            <>
              First time here?{' '}
              <button type="button" className="linkish" onClick={() => switchTo('up')}>
                Create an account
              </button>
            </>
          )}
        </p>
      </div>
    </main>
  );
}
